import React, { useEffect, useMemo, useState } from 'react'
import {
  Select, MenuItem, FormControl, InputLabel, TextField, CircularProgress
} from '@mui/material'
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  AutoAwesome as SparklesIcon,
  CheckCircle as CheckIcon,
  Folder as FolderIcon,
  CameraAlt as CameraIcon,
} from '@mui/icons-material'
import AppHeader from '../components/common/AppHeader'
import { useAuth } from '../contexts/AuthContext'
import apiClient from '../api/client'
import GalleryModal, { GalleryItem } from '../components/stylista/GalleryModal'

// #T# DressingRoomPage — Przymierzelnia
// Galeria twarzy:     /stylista/faces/woman/female-faces.json + man/male-faces.json (filter status=done)
// Galeria sylwetek:   /stylista/figure/manifest.json (same-origin via nginx)
// !! NIE używaj /mobile/v1/presets/* — presigned S3 URLs blokują CORS przy fetch()
// Generowanie:        POST /api/stylista/generate (multipart)

interface FaceJson { name: string; path: string; category: { gender: string; age: string; ethnicity: string; hair_color?: string }; status: string }
interface FigureJson { id: string; name: string; file: string; group: string }
interface ClothesJson { name: string; path: string; category: { gender: string; type: string; subtype: string; color: string }; status: string; group?: string; subgroup?: string; generated_suffix?: string }

const DR_SLOTS = [
  { field: 'face_image',           promptLine: "use the model's face (identity reference)" },
  { field: 'silhouette_image',     promptLine: "use the model's body shape" },
  { field: 'garment_top_image',    promptLine: "use the photo to dress the model" },
  { field: 'garment_bottom_image', promptLine: "use the photo to dress the model" },
  { field: 'bag_image',            promptLine: "bag" },
  { field: 'glasses_image',        promptLine: "glasses" },
] as const

type GalleryModalType = 'faces' | 'figures' | 'clothes-top' | 'clothes-bottom' | null

const MODEL_OPTIONS = [
  { value: 'strawberry-pro2', label: 'Strawberry Pro 2' },
  { value: 'strawberry-pro',  label: 'Strawberry Pro' },
  { value: 'strawberry',      label: 'Strawberry' },
  { value: 'blueberry-45',    label: 'Blueberry 4.5' },
  { value: 'blueberry',       label: 'Blueberry' },
  { value: 'lemon-lux',       label: 'Lemon Lux' },
  { value: 'lemon-max',       label: 'Lemon Max' },
]

// ---- FileUploadSlot ----
interface FileUploadSlotProps {
  label: string
  description?: string
  file: File | null
  onFileSelect: (file: File | null) => void
  showCamera?: boolean
}

const FileUploadSlot: React.FC<FileUploadSlotProps> = ({ label, description, file, onFileSelect, showCamera }) => {
  const [dragOver, setDragOver] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!file) { setPreviewUrl(null); return }
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false)
    const f = e.dataTransfer.files?.[0]
    if (f && f.type.startsWith('image/')) onFileSelect(f)
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      className={`h-full rounded-xl border-2 transition-all duration-200 cursor-pointer bg-white
        ${dragOver ? 'border-indigo-400 bg-indigo-50' : file ? 'border-green-400' : 'border-dashed border-gray-200'}`}
    >
      <div className="p-3">
        {file && previewUrl ? (
          <div className="relative">
            <img src={previewUrl} alt={label}
              className="w-full h-48 object-contain rounded-lg mb-2 bg-gray-50" />
            <button onClick={() => onFileSelect(null)}
              className="absolute top-2 right-2 p-1 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors">
              <CloseIcon fontSize="small" />
            </button>
            <div className="flex items-center gap-1.5">
              <CheckIcon className="text-green-500" fontSize="small" />
              <span className="text-sm text-green-600 font-medium truncate">{file.name}</span>
            </div>
            <span className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</span>
          </div>
        ) : (
          <div className="flex flex-col items-center py-4">
            <div className="w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mb-3">
              <ImageIcon className="text-indigo-400" sx={{ fontSize: 28 }} />
            </div>
            <p className="font-semibold text-gray-700 text-sm mb-1">{label}</p>
            {description && <p className="text-xs text-gray-400 text-center mb-3">{description}</p>}
            <div className="flex gap-2 w-full justify-center">
              <label className="flex items-center gap-1.5 px-3 py-2 text-xs border border-gray-300 rounded-lg
                                hover:border-indigo-400 cursor-pointer transition-colors min-h-[44px]">
                <UploadIcon fontSize="small" className="text-gray-500" />
                Wybierz plik
                <input type="file" hidden accept="image/*" onChange={(e) => onFileSelect(e.target.files?.[0] || null)} />
              </label>
              {showCamera && (
                <label className="flex items-center gap-1.5 px-3 py-2 text-xs border border-gray-300 rounded-lg
                                  hover:border-indigo-400 cursor-pointer transition-colors min-h-[44px]">
                  <CameraIcon fontSize="small" className="text-gray-500" />
                  Aparat
                  <input type="file" hidden accept="image/*" capture="user" onChange={(e) => onFileSelect(e.target.files?.[0] || null)} />
                </label>
              )}
            </div>
            <p className="text-xs text-gray-300 mt-2">Możesz też przeciągnąć i upuścić</p>
          </div>
        )}
      </div>
    </div>
  )
}

// ---- ReferenceTile ----
interface ReferenceTileProps {
  children: React.ReactNode
  title?: string
  hasGallery?: boolean
  onGalleryClick?: () => void
}

const ReferenceTile: React.FC<ReferenceTileProps> = ({ children, title, hasGallery, onGalleryClick }) => (
  <div className="flex flex-col h-full border border-gray-200 rounded-xl bg-white overflow-hidden
                  transition-shadow hover:shadow-md hover:border-green-400">
    <div className="flex justify-between items-center px-3 py-2 bg-gray-50 border-b border-gray-200 min-h-[48px]">
      <span className="text-xs text-gray-500 font-medium">{title || ''}</span>
      {hasGallery && (
        <button onClick={onGalleryClick}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-green-700 text-green-700
                     rounded-lg hover:bg-green-50 transition-colors min-h-[36px]">
          <FolderIcon sx={{ fontSize: 14 }} />
          Galeria
        </button>
      )}
    </div>
    <div className="flex-1 p-2">{children}</div>
  </div>
)

// ---- AccessoryRow — okulary lub torebka w jednej linii ----
const AccessoryRow: React.FC<{ label: string; file: File | null; onFileSelect: (f: File | null) => void }> = ({ label, file, onFileSelect }) => (
  <div>
    <p className="text-xs text-gray-500 font-medium mb-1">{label}</p>
    {file ? (
      <div className="flex items-center gap-2 px-2 py-1.5 border border-green-400 rounded-lg">
        <CheckIcon className="text-green-500" fontSize="small" />
        <span className="text-xs text-green-600 flex-1 truncate">{file.name}</span>
        <button onClick={() => onFileSelect(null)} className="p-0.5 hover:text-red-500 transition-colors">
          <CloseIcon fontSize="small" />
        </button>
      </div>
    ) : (
      <label className="flex items-center justify-center gap-1.5 w-full py-2 text-xs border border-gray-300
                        rounded-lg hover:border-indigo-400 cursor-pointer transition-colors min-h-[44px]">
        <UploadIcon fontSize="small" className="text-gray-400" />
        Wgraj {label.toLowerCase()}
        <input type="file" hidden accept="image/*" onChange={(e) => onFileSelect(e.target.files?.[0] || null)} />
      </label>
    )}
  </div>
)

// ---- Main page ----
const DressingRoomPage: React.FC = () => {
  const { user, logout, isAdmin } = useAuth()

  const [faceImage,          setFaceImage]          = useState<File | null>(null)
  const [silhouetteImage,    setSilhouetteImage]    = useState<File | null>(null)
  const [garmentTopImage,    setGarmentTopImage]    = useState<File | null>(null)
  const [garmentBottomImage, setGarmentBottomImage] = useState<File | null>(null)
  const [glassesImage,       setGlassesImage]       = useState<File | null>(null)
  const [bagImage,           setBagImage]           = useState<File | null>(null)

  const [galleryFaces,   setGalleryFaces]   = useState<GalleryItem[]>([])
  const [galleryFigures, setGalleryFigures] = useState<GalleryItem[]>([])
  const [galleryClothes, setGalleryClothes] = useState<GalleryItem[]>([])
  const [galleryLoading, setGalleryLoading] = useState<Record<string, boolean>>({})
  const [galleryError,   setGalleryError]   = useState<string | null>(null)
  const [galleryModalType, setGalleryModalType] = useState<GalleryModalType>(null)

  const [selectedModel,       setSelectedModel]       = useState('strawberry-pro2')
  const [aspectRatio,         setAspectRatio]         = useState('2:3')
  const [prompt,              setPrompt]              = useState('')
  const [fullPromptOverride,  setFullPromptOverride]  = useState('')
  const [loading,             setLoading]             = useState(false)
  const [error,               setError]               = useState<string | null>(null)
  const [result,              setResult]              = useState<any>(null)

  // #C# NIE zmieniaj na apiClient.get('/mobile/v1/presets/*') — presigned S3 URLs blokują fetch() CORS
  const loadGallery = async (kind: 'faces' | 'figures' | 'clothes-top' | 'clothes-bottom') => {
    const cacheKey = (kind === 'clothes-top' || kind === 'clothes-bottom') ? 'clothes' : kind
    if (galleryLoading[cacheKey]) return
    setGalleryLoading(prev => ({ ...prev, [cacheKey]: true }))
    setGalleryError(null)
    try {
      if (kind === 'faces') {
        const [femaleResp, maleResp] = await Promise.all([
          fetch('/stylista/faces/woman/female-faces.json'),
          fetch('/stylista/faces/man/male-faces.json'),
        ])
        const [femaleData, maleData] = await Promise.all([femaleResp.json(), maleResp.json()])
        const allFaces: FaceJson[] = [
          ...(Array.isArray(femaleData) ? femaleData : []),
          ...(Array.isArray(maleData)   ? maleData   : []),
        ].filter(f => f.status === 'done')
        setGalleryFaces(allFaces.map(f => ({
          id: f.name, name: f.name.replace(/_/g, ' '),
          thumbnail_url: `/stylista/faces/${f.path}/${f.name}_strawberry_pro_miniature.jpg`,
          image_url:     `/stylista/faces/${f.path}/${f.name}_strawberry_pro.jpg`,
          gender: f.category.gender, age: f.category.age,
          ethnicity: f.category.ethnicity, hair_color: f.category.hair_color,
        })))
      } else if (kind === 'figures') {
        const resp = await fetch('/stylista/figure/manifest.json')
        const data = await resp.json()
        setGalleryFigures(((data.items || []).filter((f: any) =>
          f.group === 'woman_basic' || f.group === 'man_basic'
        )).map((f: any) => ({
          id: f.id, name: f.name.replace(/_strawberry_pro$/i, '').replace(/_/g, ' '),
          thumbnail_url: `/stylista/figure/${f.file}`, image_url: `/stylista/figure/${f.file}`,
          group: f.group, gender: f.gender, figure_type: f.figure_type,
          description: f.description, height_range: f.height_range,
        })))
      } else {
        const [femaleResp, maleResp] = await Promise.all([
          fetch('/stylista/clothes/female/female-clothes.json'),
          fetch('/stylista/clothes/male/male-clothes.json'),
        ])
        const [femaleData, maleData] = await Promise.all([femaleResp.json(), maleResp.json()])
        setGalleryClothes(([
          ...(Array.isArray(femaleData) ? femaleData : []),
          ...(Array.isArray(maleData)   ? maleData   : []),
        ] as ClothesJson[]).filter(i => i.status === 'generated').map(i => ({
          id: i.name, name: i.name.replace(/^(Female|Male)_/, '').replace(/_/g, ' '),
          thumbnail_url: `/stylista/clothes/${i.path}/${i.name}_${i.generated_suffix || 'strawberry_pro'}_miniature.jpg`,
          image_url:     `/stylista/clothes/${i.path}/${i.name}_${i.generated_suffix || 'strawberry_pro'}.jpg`,
          group: i.group || i.category.type, subgroup: i.subgroup || i.category.type, gender: i.category.gender,
          description: `${i.category.subtype} · ${i.category.color}`,
        })))
      }
    } catch {
      setGalleryError('Nie udało się wczytać galerii.')
    } finally {
      setGalleryLoading(prev => ({ ...prev, [cacheKey]: false }))
    }
  }

  const openGallery = (type: GalleryModalType) => {
    if (type) {
      const cacheKey = (type === 'clothes-top' || type === 'clothes-bottom') ? 'clothes' : type
      const loaded = cacheKey === 'faces' ? galleryFaces.length > 0
        : cacheKey === 'figures' ? galleryFigures.length > 0 : galleryClothes.length > 0
      if (!galleryLoading[cacheKey] && !loaded) loadGallery(type)
    }
    setGalleryModalType(type)
  }

  const pickGalleryImage = async (item: GalleryItem, setFile: (f: File | null) => void) => {
    const src = item.image_url || item.thumbnail_url || ''
    if (!src) { setGalleryError('Brak URL obrazu w galerii.'); return }
    setGalleryError(null)
    try {
      const res = await fetch(src)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const blob = await res.blob()
      const ext = (src.split('.').pop() || '').split('?')[0] || 'jpg'
      setFile(new File([blob], `${(item.name || 'gallery').replace(/\s+/g, '-')}.${ext}`, { type: blob.type || 'image/jpeg' }))
    } catch (err: any) {
      setGalleryError(`Nie mogę pobrać obrazu: ${err.message}`)
    }
  }

  const handleGallerySelect = (item: GalleryItem) => {
    if (galleryModalType === 'faces')          pickGalleryImage(item, setFaceImage)
    else if (galleryModalType === 'figures')   pickGalleryImage(item, setSilhouetteImage)
    else if (galleryModalType === 'clothes-top')    pickGalleryImage(item, setGarmentTopImage)
    else if (galleryModalType === 'clothes-bottom') pickGalleryImage(item, setGarmentBottomImage)
    setGalleryModalType(null)
  }

  const getGalleryItems = (): GalleryItem[] => {
    if (galleryModalType === 'faces')   return galleryFaces
    if (galleryModalType === 'figures') return galleryFigures
    return galleryClothes
  }

  const getGalleryTitle = (): string => {
    if (galleryModalType === 'faces')          return 'Galeria twarzy'
    if (galleryModalType === 'figures')        return 'Galeria sylwetek'
    if (galleryModalType === 'clothes-top')    return 'Galeria ubrań — góra'
    if (galleryModalType === 'clothes-bottom') return 'Galeria ubrań — dół'
    return 'Galeria ubrań'
  }

  const slotFileMap: Record<string, File | null> = {
    face_image: faceImage, silhouette_image: silhouetteImage,
    garment_top_image: garmentTopImage, garment_bottom_image: garmentBottomImage,
    bag_image: bagImage, glasses_image: glassesImage,
  }

  const advancedPrompt = useMemo(() => {
    const header = ['Reference images (order matters):']
    let idx = 1
    DR_SLOTS.forEach(slot => {
      if (slotFileMap[slot.field]) { header.push(`Image ${idx}: ${slot.promptLine}.`); idx++ }
    })
    if (header.length > 1) header.push('Keep the same face/identity across outputs.')
    const body = prompt.trim()
      ? `User prompt:\n${prompt.trim()}`
      : 'Task:\nCreate a photorealistic fashion image. Full body shot, model wearing the provided garments, white studio background.'
    return (header.join('\n') + '\n\n' + body).trim()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faceImage, silhouetteImage, garmentTopImage, garmentBottomImage, bagImage, glassesImage, prompt])

  useEffect(() => { setFullPromptOverride('') }, [advancedPrompt])

  const hasAnyFile = !!(faceImage || garmentTopImage || silhouetteImage || garmentBottomImage || glassesImage || bagImage)
  const canGenerate = hasAnyFile && !loading

  const handleGenerate = async () => {
    if (!hasAnyFile) { setError('Dodaj przynajmniej jedno zdjęcie referencyjne'); return }
    setLoading(true); setError(null); setResult(null)
    try {
      const fd = new FormData()
      if (faceImage)          fd.append('face_image', faceImage)
      if (silhouetteImage)    fd.append('silhouette_image', silhouetteImage)
      if (garmentTopImage)    fd.append('garment_top_image', garmentTopImage)
      if (garmentBottomImage) fd.append('garment_bottom_image', garmentBottomImage)
      if (bagImage)           fd.append('bag_image', bagImage)
      if (glassesImage)       fd.append('glasses_image', glassesImage)
      fd.append('selected_model', isAdmin ? selectedModel : 'strawberry-pro')
      fd.append('username', user?.username || 'Default')
      fd.append('project_name', 'DressingRoom')
      fd.append('aspect_ratio', isAdmin ? aspectRatio : '2:3')
      if (isAdmin && fullPromptOverride.trim()) fd.append('full_prompt', fullPromptOverride.trim())
      else if (isAdmin && prompt.trim())        fd.append('figure_prompt', prompt.trim())
      const response = await apiClient.post('stylista/generate', fd, {
        headers: { 'Content-Type': null },
        timeout: 180000,
      })
      if (response.data.success) setResult(response.data)
      else setError(response.data.message || 'Błąd generowania')
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Błąd generowania')
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFaceImage(null); setSilhouetteImage(null)
    setGarmentTopImage(null); setGarmentBottomImage(null)
    setGlassesImage(null); setBagImage(null)
    setResult(null); setError(null)
  }

  const resultUrls: string[] = result
    ? [...(result.preview_files || []), ...(result.image_url ? [result.image_url] : [])]
    : []

  return (
    <>
      <AppHeader title="Przymierzelnia" username={user?.username} showAdminButton={isAdmin} showBackButton onLogout={logout} />

      <div className="max-w-5xl mx-auto px-4 py-6">

        {/* Nagłówek */}
        <div className="flex items-center gap-3 mb-6 p-4 rounded-xl bg-indigo-50/60 border border-indigo-100">
          <SparklesIcon className="text-indigo-500" sx={{ fontSize: 32 }} />
          <div>
            <h4 className="font-bold text-xl text-gray-800">👗 Przymierzelnia</h4>
            <p className="text-sm text-gray-500">Przymierz ubrania na zdjęciu twarzy — dodaj sylwetkę, góra/dół garderoby i dodatki.</p>
          </div>
        </div>

        {/* Błąd galerii */}
        {galleryError && (
          <div className="flex items-center gap-2 mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
            <span className="flex-1">{galleryError}</span>
            <button onClick={() => setGalleryError(null)} className="p-0.5 hover:text-amber-600">
              <CloseIcon fontSize="small" />
            </button>
          </div>
        )}

        {/* Zdjęcia referencyjne */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 mb-5">
          <h6 className="font-semibold text-gray-700 mb-3">Zdjęcia referencyjne</h6>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">

            {/* 1. Twoje zdjęcie */}
            <ReferenceTile title="Twoje zdjęcie">
              <FileUploadSlot
                label="Sylwetka lub twarz"
                description="Zrób zdjęcie całej sylwetki lub twarzy."
                file={faceImage} onFileSelect={setFaceImage} showCamera
              />
            </ReferenceTile>

            {/* 2. Sylwetka */}
            <ReferenceTile title="Sylwetka (opcjonalne)" hasGallery onGalleryClick={() => openGallery('figures')}>
              <FileUploadSlot
                label="Sylwetka z galerii"
                description="Wybierz z galerii lub wgraj własną."
                file={silhouetteImage} onFileSelect={setSilhouetteImage}
              />
            </ReferenceTile>

            {/* 3. Ubranie góra */}
            <ReferenceTile title="Ubranie góra" hasGallery onGalleryClick={() => openGallery('clothes-top')}>
              <FileUploadSlot
                label="Ubranie góra"
                description="Top, bluzka, sukienka, kurtka…"
                file={garmentTopImage} onFileSelect={setGarmentTopImage}
              />
            </ReferenceTile>

            {/* 4. Ubranie dół */}
            <ReferenceTile title="Ubranie dół" hasGallery onGalleryClick={() => openGallery('clothes-bottom')}>
              <FileUploadSlot
                label="Ubranie dół"
                description="Spodnie, spódnica (opcjonalne)"
                file={garmentBottomImage} onFileSelect={setGarmentBottomImage}
              />
            </ReferenceTile>

            {/* 5. Dodatki */}
            <div className="flex flex-col border border-gray-200 rounded-xl bg-white overflow-hidden hover:shadow-md hover:border-green-400 transition-shadow">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 min-h-[48px] flex items-center">
                <span className="text-xs text-gray-500 font-medium">Dodatki — Okulary + Torebka</span>
              </div>
              <div className="flex flex-col gap-3 p-3 flex-1">
                <AccessoryRow label="Okulary"            file={glassesImage} onFileSelect={setGlassesImage} />
                <AccessoryRow label="Torebka / Akcesorium" file={bagImage}  onFileSelect={setBagImage} />
              </div>
            </div>

          </div>
        </div>

        {/* Ustawienia — tylko admin */}
        {isAdmin && (
          <>
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 mb-5">
              <h6 className="font-semibold text-gray-700 mb-3">Ustawienia</h6>
              <div className="flex flex-wrap gap-3">
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel>Model AI</InputLabel>
                  <Select value={selectedModel} label="Model AI" onChange={(e) => setSelectedModel(e.target.value)}>
                    {MODEL_OPTIONS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 180 }}>
                  <InputLabel>Proporcje (AR)</InputLabel>
                  <Select value={aspectRatio} label="Proporcje (AR)" onChange={(e) => setAspectRatio(e.target.value)}>
                    <MenuItem value="none">Oryginalny</MenuItem>
                    <MenuItem value="2:3">Portret (2:3)</MenuItem>
                    <MenuItem value="1:1">Kwadrat (1:1)</MenuItem>
                    <MenuItem value="9:16">Pion (9:16)</MenuItem>
                    <MenuItem value="4:3">Standard (4:3)</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 mb-5">
              <h6 className="font-semibold text-gray-700 mb-3">Opis (prompt)</h6>
              <TextField fullWidth multiline rows={3} value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Dodaj własny opis (styl, tło, oświetlenie, kolory)…" />
            </div>

            <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4 mb-5">
              <h6 className="font-semibold text-gray-700 mb-1">Advanced — pełny prompt wysyłany do modelu</h6>
              <p className="text-xs text-gray-400 mb-3">Podgląd promptu generowanego na podstawie uzupełnionych slotów. Możesz go nadpisać.</p>
              <TextField fullWidth multiline minRows={6}
                value={fullPromptOverride !== '' ? fullPromptOverride : advancedPrompt}
                onChange={(e) => setFullPromptOverride(e.target.value)}
                placeholder="Prompt będzie widoczny po dodaniu zdjęć."
                helperText="Nadpisz prompt lub zostaw puste aby użyć automatycznego."
                sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
              />
            </div>
          </>
        )}

        {/* Błąd generowania */}
        {error && (
          <div className="flex items-center gap-2 mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <span className="flex-1">{error}</span>
            <button onClick={() => setError(null)}><CloseIcon fontSize="small" /></button>
          </div>
        )}

        {/* Przyciski akcji */}
        <div className="sticky bottom-0 z-20 -mx-4 px-4 py-3 bg-white/95 backdrop-blur-sm border-t border-gray-100 flex gap-3 sm:static sm:mx-0 sm:px-0 sm:py-0 sm:bg-transparent sm:border-0 sm:mb-6">
          <button
            onClick={handleGenerate}
            disabled={!canGenerate}
            data-testid="dressing-room-generate-button"
            className="flex-1 flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl font-semibold text-base
                       bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed
                       shadow-md transition-colors min-h-[52px]"
          >
            {loading
              ? <><CircularProgress size={20} color="inherit" /> Generowanie…</>
              : <><SparklesIcon /> Generuj</>
            }
          </button>
          <button
            onClick={handleReset}
            disabled={loading}
            className="px-6 py-3.5 rounded-xl border border-gray-300 text-gray-600 font-medium
                       hover:border-gray-400 hover:bg-gray-50 disabled:opacity-40 transition-colors min-h-[52px]"
          >
            Reset
          </button>
        </div>

        {/* Wyniki */}
        {result && resultUrls.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 mb-5">
            <h6 className="font-semibold text-gray-700 mb-3">
              Wyniki — {result.images_generated || resultUrls.length} obraz(ów)
            </h6>
            {result.success && (
              <div className="mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                {result.message || 'Wygenerowano pomyślnie!'}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {resultUrls.map((url: string, idx: number) => (
                <img key={idx} src={url} alt={`Wynik ${idx + 1}`}
                  className="w-full rounded-xl shadow-sm" />
              ))}
            </div>
          </div>
        )}

        {/* Prompt wysłany — tylko admin */}
        {isAdmin && result?.metadata?.stylista_prompt && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 mb-5">
            <p className="text-sm font-medium text-gray-600 mb-2">Prompt wysłany do modelu:</p>
            <TextField fullWidth multiline minRows={4} value={result.metadata.stylista_prompt}
              InputProps={{ readOnly: true }}
              sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
            />
          </div>
        )}

      </div>

      <GalleryModal
        open={galleryModalType !== null}
        onClose={() => setGalleryModalType(null)}
        onSelect={handleGallerySelect}
        items={getGalleryItems()}
        loading={!!galleryLoading[
          (galleryModalType === 'clothes-top' || galleryModalType === 'clothes-bottom') ? 'clothes' : (galleryModalType || '')
        ]}
        title={getGalleryTitle()}
        defaultGroup={
          galleryModalType === 'faces' ? 'european' :
          (galleryModalType === 'clothes-top' || galleryModalType === 'clothes-bottom') ? 'skirts' : null
        }
        defaultGender={
          galleryModalType === 'faces' ? 'woman' :
          galleryModalType === 'figures' ? 'woman' :
          (galleryModalType === 'clothes-top' || galleryModalType === 'clothes-bottom') ? 'female' : null
        }
        defaultAge={galleryModalType === 'faces' ? '20plus' : null}
        groupLabel={galleryModalType === 'faces' ? 'Pochodzenie:' : 'Kategoria:'}
      />
    </>
  )
}

export default DressingRoomPage
